import { useCallback, useEffect, useMemo } from 'react';

import { useSelector } from 'react-redux';

import type { ScopedCache } from '@/lib/storage/scopedCache';
import { selectUserId } from '@/redux/user/selectors';

export const useScopedCache = <TValue, TKey>(
  cache: ScopedCache<TValue, TKey>,
  entryId: string,
  invalidationKey: TKey | null
) => {
  const userId = useSelector(selectUserId);

  const cachedData = useMemo(
    () => (userId ? cache.read(userId, entryId, invalidationKey) : null),
    [cache, userId, entryId, invalidationKey]
  );

  useEffect(() => {
    if (userId && cachedData) {
      // External-system sync (localStorage LRU bookkeeping), not state passed to a parent.
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
      cache.touch(userId, entryId);
    }
  }, [cache, userId, entryId, cachedData]);

  const write = useCallback(
    (value: TValue, key: TKey) => {
      if (userId) {
        cache.write(userId, entryId, value, key);
      }
    },
    [cache, userId, entryId]
  );

  return { cachedData, write };
};
